import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Button,
  Checkbox,
  Chip,
  Input,
  Switch,
  Table,
} from "@heroui/react";

import { PickupDay } from "@/features/pickup-schedule/type.ts";
import {
  usePickupSchedule,
  usePickupScheduleConfig,
  useRegeneratePickupSchedule,
  useUpdatePickupDay,
  useUpdatePickupScheduleConfig,
} from "@/features/pickup-schedule/use-pickup-schedule.ts";
import { EditPickupDayModal } from "@/features/pickup-schedule/ui/edit-pickup-day-modal.tsx";
import { BulkEditModal } from "@/features/pickup-schedule/ui/bulk-edit-modal.tsx";

export const Route = createFileRoute("/dashboard/pickup-schedule")({
  component: RouteComponent,
});

// Инлайновый переключатель активности дня.
function ActiveToggle({ day }: { day: PickupDay }) {
  const { mutateAsync } = useUpdatePickupDay();

  return (
    <Switch
      isSelected={day.active}
      onChange={(active) => mutateAsync({ date: day.date, patch: { active } })}
    >
      <Switch.Control>
        <Switch.Thumb />
      </Switch.Control>
    </Switch>
  );
}

function ConfigControl() {
  const { data } = usePickupScheduleConfig();
  const { mutateAsync, isPending } = useUpdatePickupScheduleConfig();
  const [days, setDays] = useState<string>("");

  const current = data?.data.daysToShow ?? 7;
  const value = days === "" ? String(current) : days;

  return (
    <div className="flex flex-row items-end gap-2">
      <div className="flex flex-col gap-1">
        <span className="text-xs text-default-500">Дней в приложении</span>
        <Input
          className="w-28"
          inputMode="numeric"
          value={value}
          onChange={(e) => setDays(e.target.value.replace(/\D/g, ""))}
        />
      </div>
      <Button
        isPending={isPending}
        variant="secondary"
        onPress={() =>
          mutateAsync({ daysToShow: Number(value) || 7 }).then(() =>
            setDays(""),
          )
        }
      >
        Сохранить
      </Button>
    </div>
  );
}

function RouteComponent() {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const { data } = usePickupSchedule(year);
  const regenerate = useRegeneratePickupSchedule();

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const days = data?.data ?? [];

  const allSelected = useMemo(
    () => days.length > 0 && days.every((d) => selected.has(d.date)),
    [days, selected],
  );

  const toggle = (date: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(days.map((d) => d.date)));
  };

  return (
    <div className="flex flex-col pt-4">
      <div className="flex flex-row gap-2 justify-between items-center">
        <p className="font-bold text-xl">Расписание забора</p>

        <div className="flex flex-row items-end gap-3">
          <ConfigControl />

          <div className="flex flex-col gap-1">
            <span className="text-xs text-default-500">Год</span>
            <Input
              className="w-24"
              inputMode="numeric"
              value={String(year)}
              onChange={(e) => {
                const v = Number(e.target.value.replace(/\D/g, ""));
                if (v) setYear(v);
              }}
            />
          </div>

          <Button
            isPending={regenerate.isPending}
            variant="ghost"
            onPress={() => regenerate.mutateAsync()}
          >
            Догенерировать год
          </Button>
        </div>
      </div>

      <div className="flex flex-row items-center gap-3 mt-4">
        <BulkEditModal
          dates={[...selected]}
          onDone={() => setSelected(new Set())}
        />
        {selected.size > 0 && (
          <Button variant="ghost" onPress={() => setSelected(new Set())}>
            Снять выделение
          </Button>
        )}
      </div>

      <Table.Root className="mt-6">
        <Table.Content aria-label="Расписание забора">
          <Table.Header>
            <Table.Column>
              <Checkbox
                slot={null}
                isSelected={allSelected}
                onChange={toggleAll}
              >
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
              </Checkbox>
            </Table.Column>
            <Table.Column>Дата</Table.Column>
            <Table.Column>Подпись</Table.Column>
            <Table.Column>Активна</Table.Column>
            <Table.Column>Времена</Table.Column>
            <Table.Column>Описание</Table.Column>
            <Table.Column> </Table.Column>
          </Table.Header>
          <Table.Body items={days}>
            {(day: PickupDay) => (
              <Table.Row id={day.date}>
                <Table.Cell>
                  <Checkbox
                    slot={null}
                    isSelected={selected.has(day.date)}
                    onChange={() => toggle(day.date)}
                  >
                    <Checkbox.Control>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                  </Checkbox>
                </Table.Cell>
                <Table.Cell>{day.date}</Table.Cell>
                <Table.Cell>{day.displayDate}</Table.Cell>
                <Table.Cell>
                  <ActiveToggle day={day} />
                </Table.Cell>
                <Table.Cell>
                  <div className="flex flex-row flex-wrap gap-1">
                    {day.times.map((t) => (
                      <Chip key={t.value} variant="secondary">
                        {t.displayTime}
                      </Chip>
                    ))}
                  </div>
                </Table.Cell>
                <Table.Cell>{day.description}</Table.Cell>
                <Table.Cell>
                  <div className="flex flex-row justify-end">
                    <EditPickupDayModal day={day} />
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Content>
      </Table.Root>
    </div>
  );
}
